import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { IStudentLecturesProgressVm } from '../../../../api/student-lectures-progress-vm.model';
import { MyOskStudentLecturesService } from '../../../../api/api-services/my-osk-student-lectures.service';

interface StudentLectureCategory {
  examGuid: string;
  examName: string;
  isCurrentCategory: boolean;
  lecturesLearned: number;
  lecturesInCategory: number;
  /** Chapters (topics) with at least one slide whose every slide the student completed. */
  chaptersCompleted: number;
  chaptersInCategory: number;
  progress: number;
  lectures: IStudentLecturesProgressVm[];
}

@Component({
  selector: 'app-student-lectures',
  standalone: true,
  imports: [CommonModule, TableModule, ButtonModule],
  templateUrl: './student-lectures.component.html',
  styleUrl: './student-lectures.component.scss',
})
export class StudentLecturesComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) studentGuid!: string;

  private studentLecturesService = inject(MyOskStudentLecturesService);
  private request?: Subscription;

  lectures: IStudentLecturesProgressVm[] = [];
  categories: StudentLectureCategory[] = [];
  loading = false;
  error = false;

  ngOnChanges(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
  }

  load(): void {
    this.request?.unsubscribe();
    this.lectures = [];
    this.categories = [];
    this.error = false;
    this.loading = true;

    this.request = this.studentLecturesService.getAll(this.studentGuid).subscribe({
      next: data => {
        this.lectures = data;
        this.categories = this.groupByCategory(data);
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      },
    });
  }

  private groupByCategory(
    lectures: IStudentLecturesProgressVm[]
  ): StudentLectureCategory[] {
    const categories = new Map<string, StudentLectureCategory>();

    for (const lecture of lectures) {
      let category = categories.get(lecture.examGuid);
      if (!category) {
        category = {
          examGuid: lecture.examGuid,
          examName: lecture.examName,
          isCurrentCategory: lecture.isCurrentCategory,
          lecturesLearned: 0,
          lecturesInCategory: 0,
          chaptersCompleted: 0,
          chaptersInCategory: 0,
          progress: 0,
          lectures: [],
        };
        categories.set(lecture.examGuid, category);
      }

      category.lectures.push(lecture);
      category.lecturesLearned += lecture.lecturesLearned;
      category.lecturesInCategory += lecture.lecturesInCategory;
      // Chapters without any slide for this category are not counted - there is nothing to complete.
      if (lecture.lecturesInCategory > 0) {
        category.chaptersInCategory += 1;
        if (lecture.lecturesLearned >= lecture.lecturesInCategory) {
          category.chaptersCompleted += 1;
        }
      }
    }

    return Array.from(categories.values()).map(category => ({
      ...category,
      progress: category.lecturesInCategory === 0
        ? 0
        : Math.floor(
            100 * category.lecturesLearned / category.lecturesInCategory
          ),
    }));
  }
}

